// Performance monitoring and optimization utilities

// Web Vitals thresholds
const VITALS_THRESHOLDS = {
  LCP: 2500, // 2.5 seconds
  FID: 100,  // 100ms
  CLS: 0.1,  // 0.1
  FCP: 1800, // 1.8 seconds
  TTFB: 500  // 500ms
};

/**
 * Send metrics to analytics
 */
const sendToAnalytics = (metric) => {
  const { name, value, id } = metric;
  const isGood = (name === 'CLS' && value < VITALS_THRESHOLDS.CLS) || 
                (name === 'FID' && value < VITALS_THRESHOLDS.FID) ||
                (name === 'LCP' && value < VITALS_THRESHOLDS.LCP) ||
                (name === 'FCP' && value < VITALS_THRESHOLDS.FCP) ||
                (name === 'TTFB' && value < VITALS_THRESHOLDS.TTFB);

  // Log to console for development
  console.log(`[${name}]:`, value, isGood ? '✅' : '❌');

  // Send to your analytics service (e.g., Google Analytics, custom endpoint)
  if (window.gtag) {
    window.gtag('event', name, {
      event_category: 'Web Vitals',
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      event_label: id,
      non_interaction: true,
    });
  }
};

/**
 * Track Core Web Vitals
 */
export const trackWebVitals = () => {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return;
  }

  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    // Track each Web Vital
    getCLS(sendToAnalytics);
    getFID(sendToAnalytics);
    getFCP(sendToAnalytics);
    getLCP(sendToAnalytics);
    getTTFB(sendToAnalytics);

    // Track additional metrics
    if (window.performance) {
      // First Input Delay (FID) polyfill for older browsers
      if (!('PerformanceObserver' in window) || !('PerformanceEventTiming' in window)) {
        let firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
        
        document.addEventListener('visibilitychange', (event) => {
          firstHiddenTime = Math.min(firstHiddenTime, event.timeStamp);
        }, { once: true });

        const handleFirstInput = (event) => {
          if (event.cancelable) {
            const firstInputDelay = event.timeStamp - firstHiddenTime;
            const firstInputDelaySeconds = Math.round(firstInputDelay);
            
            if (firstInputDelaySeconds > 0) {
              sendToAnalytics({
                name: 'FID',
                value: firstInputDelaySeconds,
                id: 'v1-'+Date.now()+'-'+Math.floor(Math.random()*9e12).toString(36)
              });
              
              // Only track the first input
              window.removeEventListener('input', handleFirstInput, true);
            }
          }
        };

        window.addEventListener('input', handleFirstInput, { capture: true, once: true });
      }
    }
  }).catch(console.error);
};

/**
 * Preload critical resources
 */
export const preloadCriticalResources = () => {
  // Preload critical fonts
  const fontLinks = [
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap'
  ];

  fontLinks.forEach(href => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    link.onload = function() { this.rel = 'stylesheet'; };
    document.head.appendChild(link);
  });

  // Preload critical images
  const criticalImages = [
    '/logo.png',
    '/hero-bg.jpg'
  ];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

/**
 * Debounce function for performance optimization
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * Throttle function for performance optimization
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Intersection Observer for lazy loading
 */
export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };

  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    callback({ isIntersecting: true });
    return { observe: () => {}, disconnect: () => {} };
  }

  return new IntersectionObserver(callback, defaultOptions);
};

/**
 * Optimize images for better performance
 */
export const optimizeImage = (src, options = {}) => {
  const { width, height, quality = 80, format = 'webp' } = options;
  
  // If using a CDN like Cloudinary or ImageKit, construct optimized URL
  if (src.includes('cloudinary.com')) {
    const transformations = [];
    if (width) transformations.push(`w_${width}`);
    if (height) transformations.push(`h_${height}`);
    transformations.push(`q_${quality}`);
    transformations.push(`f_${format}`);
    
    return src.replace('/upload/', `/upload/${transformations.join(',')}/`);
  }
  
  // For other CDNs or services, return original src
  return src;
};

/**
 * Prefetch resources for better navigation performance
 */
export const prefetchRoute = (routePath) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = routePath;
  document.head.appendChild(link);
};

/**
 * Critical CSS inlining helper
 */
export const inlineCriticalCSS = (css) => {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = css;
  document.head.appendChild(style);
};

/**
 * Service Worker registration for caching
 */
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('SW registered: ', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  }
};

/**
 * Memory usage monitoring
 */
export const monitorMemoryUsage = () => {
  if ('memory' in performance) {
    const memInfo = performance.memory;
    console.log('Memory Usage:', {
      used: Math.round(memInfo.usedJSHeapSize / 1048576) + ' MB',
      total: Math.round(memInfo.totalJSHeapSize / 1048576) + ' MB',
      limit: Math.round(memInfo.jsHeapSizeLimit / 1048576) + ' MB'
    });
  }
};

/**
 * Bundle size tracking
 */
export const trackBundleSize = () => {
  if (typeof window !== 'undefined' && window.performance) {
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0) {
      const navigation = entries[0];
      console.log('Page Load Performance:', {
        domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
        loadComplete: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
        totalTime: Math.round(navigation.loadEventEnd - navigation.fetchStart)
      });
    }
  }
};

/**
 * Critical rendering path optimization
 */
export const optimizeCriticalRenderingPath = () => {
  // Remove render-blocking resources
  const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
  stylesheets.forEach(link => {
    if (!link.hasAttribute('critical')) {
      link.media = 'print';
      link.onload = function() { this.media = 'all'; };
    }
  });
};

/**
 * Resource hints for better performance
 */
export const addResourceHints = () => {
  const hints = [
    { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: '//www.google-analytics.com' },
    { rel: 'dns-prefetch', href: '//js.stripe.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true }
  ];

  hints.forEach(hint => {
    const link = document.createElement('link');
    Object.keys(hint).forEach(key => {
      if (key === 'crossorigin') {
        link.crossOrigin = hint[key];
      } else {
        link[key] = hint[key];
      }
    });
    document.head.appendChild(link);
  });
};
